import React, { useEffect, useState } from "react";
import { Group } from "react-konva";
import { IMondayTaskController } from "../../controller";
import { MondayTaskSection } from "shared/datamodel/monday-task";
import useObservableController from "elements/hooks/use-observable-controller";
import Hoverable from "frontend/ui-components/konva-helpers/hoverable";
import { PreviewImage } from "./preview-image";
import { CarouselButton, CarouselCounter } from "./carousel";

interface FilePreviewsProps {
  controller: IMondayTaskController;
  contentWidth: number;
}

const IMAGE_HEIGHT = 160;

export const FilePreviews = React.memo(({ controller, contentWidth }: FilePreviewsProps) => {
  useObservableController(controller, [
    "item-changed",
    "size-changed",
    "board-columns-changed",
    "file-visibility-changed",
  ]);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isImageHovered, setIsImageHovered] = useState(false);

  const y = controller.getSectionY(MondayTaskSection.IMAGE);
  const imageUrls = controller.imagePreviews();

  const imageUrl = imageUrls[currentIndex];
  const totalImages = imageUrls.length;

  // Solves an edge case where last file is hidden and we're on it
  useEffect(() => {
    if (currentIndex >= imageUrls.length) {
      setCurrentIndex(imageUrls.length > 0 ? imageUrls.length - 1 : 0);
    }
  }, [imageUrls.length, currentIndex]);

  if (!imageUrl || totalImages === 0) {
    return null;
  }

  const handlePrevious = () => {
    setCurrentIndex((previous) => (previous > 0 ? previous - 1 : totalImages - 1));
  };

  const handleNext = () => {
    setCurrentIndex((previous) => (previous < totalImages - 1 ? previous + 1 : 0));
  };

  return (
    <Group y={y}>
      {/* Hover overlay for entire image section */}
      <Hoverable width={contentWidth} height={IMAGE_HEIGHT} fill="transparent" hoverChange={setIsImageHovered} />

      <PreviewImage src={imageUrl} width={contentWidth} height={IMAGE_HEIGHT} />

      {totalImages > 1 && isImageHovered && (
        <>
          <CarouselButton x={10} y={IMAGE_HEIGHT / 2} direction="previous" onClick={handlePrevious} />
          <CarouselButton x={contentWidth - 10} y={IMAGE_HEIGHT / 2} direction="next" onClick={handleNext} />
          <CarouselCounter x={contentWidth / 2} y={IMAGE_HEIGHT} current={currentIndex + 1} total={totalImages} />
        </>
      )}
    </Group>
  );
});

FilePreviews.displayName = "FilePreviews";
